/**
 * Centralized display formatters for EHR data.
 *
 * All functions are pure — null/undefined/empty → return null.
 * Applied at render time; raw DB data stays untouched.
 */

import { DEFAULT_SPECIALTY_DRUGS } from './drugWatchlist';

// --- Abbreviations to preserve as uppercase ---
const PRESERVE_UPPER = new Set([
  'PSA', 'EHR', 'ADT', 'COPD', 'GERD', 'BPH', 'HIV', 'AIDS',
  'MD', 'DO', 'NP', 'PA', 'RN', 'LPN',
  'BCBS', 'HMO', 'PPO', 'HSA', 'EPO',
  'II', 'III', 'IV', 'Jr', 'Sr',
  'BMI', 'BP', 'A1C', 'TSH', 'CBC', 'MRI', 'CT', 'ICU', 'ER', 'OR',
  'CDK', 'PD', 'GnRH',
]);

// Suffixes that indicate "Doctor" prefix
const DR_SUFFIXES = new Set(['MD', 'DO']);

// Dosage unit pattern — kept lowercase after numbers
const DOSAGE_UNITS_RE = /(\d)(mg|mcg|ml|g|iu|units)\b/gi;

/**
 * Smart title-casing with abbreviation preservation.
 *
 * - Mixed-case input (has both upper and lower) → return as-is
 * - All-upper or all-lower → capitalize first letter of each word
 * - Words in PRESERVE_UPPER stay uppercase
 */
export function titleCase(str) {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (!trimmed) return null;

  const hasUpper = /[A-Z]/.test(trimmed);
  const hasLower = /[a-z]/.test(trimmed);

  // Mixed case — already formatted by source system
  if (hasUpper && hasLower) return trimmed;

  return trimmed
    .split(/\s+/)
    .map(word => {
      const upper = word.toUpperCase();
      if (PRESERVE_UPPER.has(upper)) return upper;
      // Preserve Jr/Sr casing
      if (upper === 'JR' || upper === 'JR.') return 'Jr.';
      if (upper === 'SR' || upper === 'SR.') return 'Sr.';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Title-case that always applies (ignores mixed-case detection).
 * Used internally for known-raw EHR flag text after a colon.
 */
function forceTitleCase(str) {
  if (!str || typeof str !== 'string') return str;
  return str.trim()
    .split(/\s+/)
    .map(word => {
      // Strip trailing punctuation for abbreviation check, reattach after
      const punctMatch = word.match(/^([A-Za-z]+)([^A-Za-z]*)$/);
      const core = punctMatch ? punctMatch[1] : word;
      const trailing = punctMatch ? punctMatch[2] : '';
      const upper = core.toUpperCase();
      if (PRESERVE_UPPER.has(upper)) return upper + trailing;
      if (upper === 'JR' || upper === 'JR.') return 'Jr.' + trailing;
      if (upper === 'SR' || upper === 'SR.') return 'Sr.' + trailing;
      return core.charAt(0).toUpperCase() + core.slice(1).toLowerCase() + trailing;
    })
    .join(' ');
}

/**
 * Format phone number to (313) 555-1031 style.
 * Moved from PatientDrawer.js local function.
 */
export function formatPhone(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

/**
 * Format EHR provider names.
 *
 * "PATEL, JOHN MD" → "Dr. John Patel"
 * "SMITH, JANE NP" → "Jane Smith, NP"
 * "Dr. Patel"      → unchanged (mixed-case)
 */
export function formatProvider(provider) {
  if (!provider || typeof provider !== 'string') return null;
  const trimmed = provider.trim();
  if (!trimmed) return null;

  // Mixed case → already formatted
  const hasUpper = /[A-Z]/.test(trimmed);
  const hasLower = /[a-z]/.test(trimmed);
  if (hasUpper && hasLower) return trimmed;

  // Try "LASTNAME, FIRSTNAME SUFFIX" pattern
  const commaMatch = trimmed.match(/^([A-Za-z'-]+)\s*,\s*([A-Za-z'-]+)(?:\s+([A-Za-z]+))?$/i);
  if (commaMatch) {
    const lastName = commaMatch[1].charAt(0).toUpperCase() + commaMatch[1].slice(1).toLowerCase();
    const firstName = commaMatch[2].charAt(0).toUpperCase() + commaMatch[2].slice(1).toLowerCase();
    const suffix = commaMatch[3] ? commaMatch[3].toUpperCase() : null;

    if (suffix && DR_SUFFIXES.has(suffix)) {
      return `Dr. ${firstName} ${lastName}`;
    }
    if (suffix) {
      return `${firstName} ${lastName}, ${suffix}`;
    }
    return `${firstName} ${lastName}`;
  }

  // Fallback to titleCase
  return titleCase(trimmed);
}

/**
 * Format insurance name using titleCase (inherits abbreviation preservation).
 */
export function formatInsurance(insurance) {
  return titleCase(insurance);
}

/**
 * Format AI reason strings — fixes lowercased EHR flag text.
 *
 * "EHR flags: rising psa" → "EHR Flags: Rising PSA"
 * "Last visit was 94 days ago..." → unchanged (already well-formed)
 */
export function formatAiReason(reason) {
  if (!reason || typeof reason !== 'string') return null;
  const trimmed = reason.trim();
  if (!trimmed) return null;

  // Match EHR-related prefix patterns and title-case the flag portion after the colon.
  // Patterns from riskRules.js:
  //   "EHR flags: rising psa, overdue tt"
  //   "Multiple EHR flags (4): overdue psa, overdue tt"
  //   "Has EHR flags: rising psa"
  //   "EHR flagged: urgent"
  //   "EHR status: needs review"
  const ehrPrefixRe = /^((?:Multiple\s+)?EHR\s+(?:flags?|flagged|status)(?:\s*\(\d+\))?|Has\s+EHR\s+flags?)\s*:\s*(.+)$/i;
  const match = trimmed.match(ehrPrefixRe);
  if (match) {
    // Force title-case the prefix (can't use titleCase — it skips mixed-case input)
    const prefix = match[1].replace(/\w+/g, w => {
      const upper = w.toUpperCase();
      if (PRESERVE_UPPER.has(upper)) return upper;
      // Keep parenthesized numbers as-is
      if (/^\d+$/.test(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    });
    const flagPart = forceTitleCase(match[2]);
    return `${prefix}: ${flagPart}`;
  }

  return trimmed;
}

/**
 * Format condition name using titleCase.
 */
export function formatCondition(condition) {
  return titleCase(condition);
}

/**
 * Format medication name using titleCase, preserving dosage units lowercase.
 *
 * "metformin 500mg" → "Metformin 500mg"
 * "COPD"            → "COPD"
 */
export function formatMedication(medication) {
  if (!medication || typeof medication !== 'string') return null;
  const result = titleCase(medication);
  if (!result) return null;
  // Restore dosage units to lowercase (titleCase may have capitalized them)
  return result.replace(DOSAGE_UNITS_RE, (_, num, unit) => `${num}${unit.toLowerCase()}`);
}

/**
 * Check if a medication string matches any drug in the specialty watchlist.
 */
export function isSpecialtyDrug(medicationString, watchlist) {
  if (!medicationString || typeof medicationString !== 'string') return false;
  const drugs = watchlist || DEFAULT_SPECIALTY_DRUGS;
  const lower = medicationString.toLowerCase();
  return drugs.some(drug => lower.includes(drug));
}

/**
 * Sentence-case an AI risk factor string. Preserves abbreviations.
 */
export function formatRiskFactor(factor) {
  if (!factor || typeof factor !== 'string') return null;
  const trimmed = factor.trim();
  if (!trimmed) return null;

  // If already has mixed case, leave it alone
  const hasUpper = /[A-Z]/.test(trimmed);
  const hasLower = /[a-z]/.test(trimmed);
  if (hasUpper && hasLower) return trimmed;

  // Sentence case: capitalize first letter, lowercase the rest,
  // but preserve abbreviations from PRESERVE_UPPER
  const words = trimmed.split(/\s+/);
  return words
    .map((word, i) => {
      const upper = word.toUpperCase();
      if (PRESERVE_UPPER.has(upper)) return upper;
      if (upper === 'JR' || upper === 'JR.') return 'Jr.';
      if (upper === 'SR' || upper === 'SR.') return 'Sr.';
      if (i === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word.toLowerCase();
    })
    .join(' ');
}
